import * as ko from 'knockout';
import {moment} from 'moment';
import {
    ICompanySyncstep,
    ICompanySyncstepParameter,
    IObjectData,
    IOrganization,
    IRouteDefinition,
    IRouteParams,
    ISyncField,
    TRouteId,
} from '../../index';
import {IDropdownButton} from '../button';
import {EMPTY_HEADER_ACTIONS, IPageShellHeaderActions} from './pageShellHeaderActions';
import {IPasswordChangeProgressParams} from '../password/progress';
import {NOT_FOUND_PREFIX, REJECTED_PREFIX} from '../password/constants';
import {nsOverride} from '../override/nsOverride';
import {nsFa} from '../fa/fn';
import {global, lm, lm_} from '../../appContext';
import {getRouteDropdownButton, setRouteHash} from '../../constants/RouteDefinitions';
import {INPUT_DATE_FORMAT, ObjectTypeId} from '../../constants';
import {SyncstepId} from '../../constants/Syncstep';
import {DELETING_MESSAGE, PLEASE_WAIT_MESSAGE} from '../../constants/Language';
import {TSyncfield} from '../../constants/Syncfields';
import PromiseRequest from '../../req/PromiseRequest';
import {IRequestObject} from '../../req/index';
import {nsSyncFields} from '../../req/nsSyncFields';
import {nsRoute} from '../../util/route';
import {
    canManageOthersPassword,
    createErrorMessage,
    getObjectDefinition,
    getObjectDisplayName,
    getOrganizationFromId,
    getRawSyncFieldValue,
    getSyncField,
    isEadmRoleGreaterOrEqual,
} from '../../util/misc';
import {getDisplayNameForRouteData, getObjectFromRouteData} from '../../util/request';
import {supportsPageShellObjectActions} from './PageShellObjectSupport';
import ButtonOrder = nsRoute.ButtonOrder;
import i = nsFa.i;

type TUserKind = 'user' | 'manualuser' | 'apiuser';

const enum ActionGroup {
    Emulation,
    Password,
    Tools,
    Destructive,
}

type TOrderedAction = IDropdownButton & {
    actionGroup: ActionGroup
    order: number
    /** Promoted to a visible button on the tab row instead of the "Mer" menu. */
    quick?: boolean
};

export interface IPageShellObjectActionsContext {
    object: IObjectData
    shellRouteDef: IRouteDefinition
    routeParams: IRouteParams
    /** Called after actions that mutate the object so the shell can refresh. */
    onObjectMutated?: () => void
}

export {supportsPageShellObjectActions};

/**
 * Builds and owns the object actions for page-shell detail routes as a hybrid command bar:
 * password (synced users) or wizard/edit (manual objects) stay visible; the rest sit behind "Mer".
 */
export class PageShellObjectActions {
    readonly headerActions: KnockoutObservable<IPageShellHeaderActions> = ko.observable(EMPTY_HEADER_ACTIONS);

    private readonly reqSendPasswordBySms = new PromiseRequest('resetpasswordbysms');
    private readonly reqGetCompanySyncsteps = global.getPromiseRequest('getcompanysyncsteps');
    private readonly reqGetAllCompanySyncstepParameters = global.getPromiseRequest('getallcompanysyncstepparameters');
    private readonly reqDelete = new PromiseRequest('deleteobject');
    private reqGetSyncfields: IRequestObject | null = null;
    private disposed = false;

    constructor(private readonly context: IPageShellObjectActionsContext) {}

    /** Load any async field metadata, then publish the tab-row actions. */
    start(): void {
        const typeId = this.context.shellRouteDef.objectTypeId;
        if (typeId == null) {
            this.headerActions(this.buildHeaderActions([]));
            return;
        }

        this.reqGetSyncfields = nsSyncFields.getSyncFields(typeId);
        this.reqGetSyncfields.request
            .then((syncfields: ISyncField[]) => {
                if (this.disposed) {
                    return;
                }
                this.headerActions(this.buildHeaderActions(syncfields));
            })
            .catch(() => {
                if (this.disposed) {
                    return;
                }
                this.headerActions(this.buildHeaderActions([]));
            });
    }

    dispose(): void {
        this.disposed = true;
        this.reqSendPasswordBySms.abort();
        this.reqGetCompanySyncsteps.abort();
        this.reqGetAllCompanySyncstepParameters.abort();
        this.reqDelete.abort();
        this.reqGetSyncfields?.promise.abort();
    }

    private buildHeaderActions(allSyncfields: ISyncField[]): IPageShellHeaderActions {
        const actions = this.collectActions(allSyncfields)
            .sort((a, b) => a.actionGroup - b.actionGroup || a.order - b.order);

        const quick = actions.filter((btn) => btn.quick && (btn.validator?.() ?? true));
        const more = actions.filter((btn) => !btn.quick && (btn.validator?.() ?? true));

        return {
            quick,
            more: more.map((action, index) => {
                if (index === 0 || action.actionGroup === more[index - 1]!.actionGroup) {
                    return action;
                }
                return {
                    ...action,
                    cls: [action.cls, 'btn-separated'].filter(Boolean).join(' '),
                };
            }),
        };
    }

    /** Assemble candidate actions with the same eligibility rules as the old heading buttons. */
    private collectActions(allSyncfields: ISyncField[]): TOrderedAction[] {
        const {object, shellRouteDef} = this.context;
        const typeId = shellRouteDef.objectTypeId!;
        const isManual = !!shellRouteDef.manual;
        const isLoggedInUser = global.login.user()?.id == object.id;
        const userKind = this.getUserKind(typeId, isManual);
        const pv = global.permissionValidator;
        const actions: TOrderedAction[] = [];

        if (userKind === 'user' && isLoggedInUser) {
            actions.push({
                ...this.routeAction('resetownpassword', ActionGroup.Password, ButtonOrder.PASSWORD),
                quick: true,
            });
        }

        if (!isManual) {
            if (typeId === ObjectTypeId.ApiUser) {
                actions.push({
                    ...this.routeAction('resetapiuserpassword', ActionGroup.Password, ButtonOrder.PASSWORD),
                    quick: true,
                });
            }
            if (typeId === ObjectTypeId.User) {
                const orgNr = global.login.selectedOrganizationId();
                const targetUserPermission: IOrganization | undefined = orgNr
                    ? getOrganizationFromId(orgNr, object.permission)
                    : undefined;
                actions.push({
                    ...this.routeAction('resetpassword', ActionGroup.Password, ButtonOrder.PASSWORD, {
                        validator: () => !isLoggedInUser
                            && !!targetUserPermission
                            && isEadmRoleGreaterOrEqual(targetUserPermission)
                            && canManageOthersPassword(),
                    }),
                    quick: true,
                });
            }
        }

        if (userKind === 'user') {
            actions.push({
                onClick: () => global.loginManager.doEmulation(getObjectFromRouteData(object)),
                icon: 'fa-sign-in',
                label: 'emulate user',
                actionGroup: ActionGroup.Emulation,
                order: ButtonOrder.EMULATE_USER,
                validator: () => pv.isMinCompanyAdmin(),
            });
        }

        if (userKind === 'user') {
            const hasSmsNumber = this.getForgotPasswordSmsFields()
                .some((key) => !!getRawSyncFieldValue(key, object.syncfields));
            if (hasSmsNumber) {
                actions.push({
                    onClick: () => this.sendPasswordAsSmsClick(),
                    icon: 'fa-comment-lock',
                    label: 'send new password by sms',
                    actionGroup: ActionGroup.Password,
                    order: ButtonOrder.SEND_PASSWORD_BY_SMS,
                    validator: () => pv.isMinDepartmentAdmin(),
                });
            }
        }

        if (userKind === 'user') {
            actions.push({
                onClick: () => this.onUpdateExpiryDateClick(),
                icon: 'fa-calendar-pencil',
                label: () => `edit subject|s:((${lm_.getInstance(ObjectTypeId.User).t('ExpiryDate')}|ac))`,
                actionGroup: ActionGroup.Tools,
                order: ButtonOrder.EDIT,
                validator: () => pv.isMinDepartmentAdmin() && !!object.derivedfrommanualobjectid,
            });
            actions.push({
                onClick: () => this.onUpdateExtendLifeClick(allSyncfields),
                icon: 'fa-calendar-pencil',
                label: () => lm_.getInstance(ObjectTypeId.User).t('ExtendLife') ?? '',
                actionGroup: ActionGroup.Tools,
                order: ButtonOrder.EDIT,
                validator: () => {
                    const field = getSyncField('ExtendLife', allSyncfields);
                    return field ? nsOverride.isSyncfieldOverrideable(field) : false;
                },
            });
        }

        // Manual shells have no password quick action — promote wizard (else edit) instead.
        if (userKind === 'manualuser') {
            actions.push({
                ...this.routeAction('wizardedituser', ActionGroup.Tools, ButtonOrder.EDIT_WIZARD),
                quick: true,
            });
        }

        // Route-def edit targets (e.g. editmanualuser) — moved here from the heading.
        if (shellRouteDef.edit) {
            const promoteEditAsQuick = isManual && !actions.some((a) => a.quick);
            actions.push({
                ...this.routeAction(shellRouteDef.edit, ActionGroup.Tools, ButtonOrder.EDIT),
                ...(promoteEditAsQuick ? {quick: true} : {}),
            });
        }

        const isApiUser = typeId === ObjectTypeId.ApiUser;
        if (!isApiUser && isManual && pv.isMinCompanyAdmin()) {
            const objStr = `manual ${getObjectDefinition(typeId)?.translation ?? 'user'}|ac`;
            actions.push({
                onClick: () => this.onDeleteClick(objStr),
                cls: 'btn-danger btn-ready-calm',
                icon: 'fa-trash-alt',
                label: `delete subject|s:((${objStr}))`,
                actionGroup: ActionGroup.Destructive,
                order: ButtonOrder.DELETE,
            });
        }
        if (isApiUser && pv.isMinCompanyAdmin()) {
            actions.push({
                onClick: () => this.onDeleteClick('apiuser'),
                cls: 'btn-danger btn-ready-calm',
                icon: 'fa-trash-alt',
                label: 'delete subject|s:((apiuser))',
                actionGroup: ActionGroup.Destructive,
                order: ButtonOrder.DELETE,
            });
        }

        return actions;
    }

    private routeAction(
        id: TRouteId,
        actionGroup: ActionGroup,
        order: number,
        config: Partial<IDropdownButton> = {},
    ): TOrderedAction {
        return {
            ...getRouteDropdownButton(id, config),
            actionGroup,
            order,
        } as TOrderedAction;
    }

    private getUserKind(typeId: ObjectTypeId, isManual: boolean): TUserKind | null {
        switch (typeId) {
            case ObjectTypeId.ApiUser:
                return 'apiuser';
            case ObjectTypeId.User:
                return isManual ? 'manualuser' : 'user';
            default:
                return null;
        }
    }

    private getForgotPasswordSmsFields(): TSyncfield[] {
        const smsfield = global.login.selectedOrganization()?.smsfield;
        return (smsfield || 'Mobile')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean) as TSyncfield[];
    }

    private sendPasswordAsSmsClick(): void {
        const object = this.context.object;
        const bindingContext = {
            smsValue: ko.observable<string | null>(null),
            available: this.getForgotPasswordSmsFields()
                .map((key: TSyncfield) => ({
                    key,
                    val: getRawSyncFieldValue(key, object.syncfields),
                    syncfield: getSyncField(key, object.syncfields),
                }))
                .filter((obj) => obj.val),
        };
        bindingContext.smsValue(bindingContext.available[0]?.key ?? null);

        global.dialogData({
            title: `${i('fa-comment-lock')} ((send new password by sms))`,
            bindingContext,
            bodyWithBindings: `<p data-bind="t: 'you are about to generate and send a new password to subject|s:${getObjectDisplayName(object)}'"></p>
<p data-bind="t: 'which phone number du you wish to use'"></p>
<div data-bind="foreach: available" class="margin--left-medium">
<div>
    <label>
        <input type="radio" name="mobilefield" data-bind="checkedValue: key, checked: $parent.smsValue">
        <!-- ko _component: {
            name: 'component/variable/syncfield-name',
            params: {
                syncfield: syncfield
            }
        } --><!-- /ko -->:
        <span data-bind="text: val"></span>
    </label>
</div>
</div>
`,
            confirmLabel: 'send message',
            cancelLabel: 'no, cancel',
            onConfirm: () => {
                global.snackbarData({message: PLEASE_WAIT_MESSAGE});
                const submittedAt = new Date();
                this.reqSendPasswordBySms
                    .request({id: object.id, mobilefield: bindingContext.smsValue()})
                    .then(async () => {
                        global.cacheManager.removeCache('getruleset');
                        global.cacheManager.removeCache('searchruleset');
                        global.cacheManager.removeCache('searchobject');
                        global.cacheManager.removeCache('getobject');
                        const showLive = await this.shouldShowLivePasswordProgress();
                        if (showLive) {
                            this.openPasswordProgressDialog(submittedAt);
                        } else {
                            global.snackbarData({message: 'password.progress.complete'});
                            this.context.onObjectMutated?.();
                        }
                    })
                    .catch((reason: string) => {
                        if (typeof reason === 'string'
                            && (reason.startsWith(REJECTED_PREFIX) || reason.startsWith(NOT_FOUND_PREFIX))) {
                            global.snackbarData({message: reason, type: 'danger'});
                        } else {
                            global.snackbarData(createErrorMessage(String(reason)));
                        }
                    });
            },
        });
    }

    private async shouldShowLivePasswordProgress(): Promise<boolean> {
        try {
            const orgnr = global.login.selectedOrganizationId();
            const steps: ICompanySyncstep[] = await this.reqGetCompanySyncsteps.request({orgnr});
            const entraSteps = (steps ?? [])
                .filter((s) => !!s?.passwordexport)
                .filter((s) => s?.syncstep?.id === SyncstepId.Export_Azure
                    || s?.syncstep?.id === SyncstepId.Export_Azure_2);

            for (const step of entraSteps) {
                const parameters: ICompanySyncstepParameter[] = await this.reqGetAllCompanySyncstepParameters.request({
                    companysyncstepid: step.id,
                    orgnr,
                });
                const p = (parameters ?? []).find((x) => x?.keyvalue === 'Verify password in real time');
                if (!p) {
                    continue;
                }
                if (typeof p.value === 'boolean') {
                    if (p.value) {
                        return true;
                    }
                } else if (typeof p.value === 'string') {
                    if (p.value.toLowerCase() === 'true') {
                        return true;
                    }
                }
            }
        } catch {
            // Prefer snackbar completion over live progress when config cannot be resolved.
        }
        return false;
    }

    private openPasswordProgressDialog(submittedAt: Date): void {
        const userId = this.context.object.id;
        let close: (() => void) | undefined;
        const params: IPasswordChangeProgressParams = {
            userId: userId ?? 0,
            submittedAt,
            onSucceeded: () => {
                global.snackbarData({message: 'password.progress.complete'});
                global.cacheManager.removeCache('getobject');
                close?.();
                this.context.onObjectMutated?.();
            },
            onFailed: (info) => {
                global.cacheManager.removeCache('getobject');
                close?.();
                global.snackbarData(createErrorMessage(info.lasterror || 'password.not.updated'));
            },
        };
        const dialogId = global.dialogManager.addComponent({
            title: `${i('fa-comment-lock')} ((send new password by sms))`,
            fullWidth: false,
            content: {
                name: 'component/password/progress',
                params,
            },
        });
        close = () => global.dialogManager.remove(dialogId);
    }

    private onUpdateExpiryDateClick(): void {
        const minDate = moment().startOf('day').format(INPUT_DATE_FORMAT);
        const maxDate = global.permissionValidator.isMinSuperManager()
            ? undefined
            : moment().startOf('day').add(3, 'months').format(INPUT_DATE_FORMAT);
        global.dialogManager.addComponent({
            title: ko.observable(`${i('fa-pencil')} ((edit))`),
            content: {
                name: 'component/object/dialog/manual.syncfield.edit',
                params: {
                    object: this.context.object,
                    syncfieldname: 'ExpiryDate',
                    inputAttributes: {min: minDate, max: maxDate},
                },
            },
        });
    }

    private onUpdateExtendLifeClick(allSyncfields: ISyncField[]): void {
        const objectData = this.context.object;
        const syncfield = getSyncField('ExtendLife', objectData.syncfields)
            ?? getSyncField('ExtendLife', allSyncfields);
        const title = `${i('fa-pencil')} ${lm_.getInstance(ObjectTypeId.User).t('ExtendLife')}`;
        global.dialogManager.addComponent({
            title,
            fullWidth: false,
            content: {
                name: 'component/object/dialog/syncfield.edit',
                params: {object: objectData, syncfield},
            },
        });
    }

    private onDeleteClick(objStr: string): void {
        const {object, shellRouteDef, routeParams} = this.context;
        const objDef = getObjectDefinition(shellRouteDef.objectTypeId);
        global.dialogManager.addConfirm({
            buttons: {
                cancel: {
                    label: 'no, cancel',
                },
                confirm: {
                    label: 'yes, continue',
                    cls: 'btn-danger',
                    onClick: () => {
                        global.snackbarData({message: DELETING_MESSAGE});
                        this.reqDelete
                            .request({id: object.id})
                            .then(() => {
                                global.cacheManager.removeCache('getruleset');
                                global.cacheManager.removeCache('searchruleset');
                                global.cacheManager.removeCache('searchobject');
                                global.cacheManager.removeCache('getobject');
                                global.snackbarData({
                                    message: `subject is deleted|s:((${objDef?.translation ?? 'object'}))`,
                                });
                                const parent = shellRouteDef.parent;
                                if (parent) {
                                    setRouteHash(parent, routeParams);
                                }
                            })
                            .catch((reason: string) => {
                                global.snackbarData(createErrorMessage(String(reason)));
                            });
                    },
                },
            },
            content: `<p>${lm.t(`message: you are about to delete subject|s:${getDisplayNameForRouteData(object, shellRouteDef, routeParams)}`)}</p>
<p>${lm.t('do you wish to continue')}</p>`,
            title: `${i('fa-trash-alt')} ((delete subject|s:((${objStr}))))`,
        });
    }
}
